import { BookOpenCheck, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { forwardRef, useImperativeHandle, useRef } from "react";

import type { CalendarDay, CalendarResponse, CalendarSession } from "@learn-flow/contracts";

import {
  formatDayName,
  formatDayNumber,
  formatMinutes,
  formatRangeLabel,
} from "../calendar/calendar-date.js";
import { calendarDayAccessibleName } from "./schedule-week-picker-label.js";

type ScheduleWeekPickerProps = {
  calendar: CalendarResponse | undefined;
  isError: boolean;
  isFetching: boolean;
  isPending: boolean;
  selectedDate: string;
  onNext: () => void;
  onPrevious: () => void;
  onRetry: () => void;
  onSelectDate: (date: string) => void;
};

export type ScheduleWeekPickerHandle = {
  focusSelectedDay: () => void;
};

export const ScheduleWeekPicker = forwardRef<ScheduleWeekPickerHandle, ScheduleWeekPickerProps>(
  function ScheduleWeekPicker(
    {
      calendar,
      isError,
      isFetching,
      isPending,
      selectedDate,
      onNext,
      onPrevious,
      onRetry,
      onSelectDate,
    },
    ref,
  ): React.JSX.Element {
    const panelRef = useRef<HTMLElement>(null);
    useImperativeHandle(
      ref,
      () => ({
        focusSelectedDay: () => {
          const selected = selectedDate
            ? panelRef.current?.querySelector<HTMLButtonElement>(
                `[data-schedule-date="${selectedDate}"]`,
              )
            : null;
          (
            selected ?? panelRef.current?.querySelector<HTMLButtonElement>(".schedule-calendar-day")
          )?.focus();
        },
      }),
      [selectedDate],
    );

    return (
      <section
        ref={panelRef}
        className="schedule-calendar-panel"
        aria-busy={isPending || isFetching}
        aria-label="Schedule calendar"
      >
        {isPending ? (
          <div className="schedule-calendar-state" role="status">
            <CalendarDays size={22} aria-hidden="true" />
            <span>Loading your seven-day calendar…</span>
          </div>
        ) : isError || !calendar ? (
          <div className="schedule-calendar-state error" role="alert">
            <strong>Your calendar could not be loaded.</strong>
            <span>Retry without losing your choices.</span>
            <button type="button" className="soft-button" onClick={onRetry}>
              Retry Calendar
            </button>
          </div>
        ) : (
          <>
            <div className="schedule-calendar-header">
              <button
                type="button"
                className="icon-button bordered"
                aria-label="Previous seven days"
                onClick={onPrevious}
              >
                <ChevronLeft size={18} aria-hidden="true" />
              </button>
              <h3>{formatRangeLabel(calendar.range.startDate, calendar.range.endDate)}</h3>
              <button
                type="button"
                className="icon-button bordered"
                aria-label="Next seven days"
                onClick={onNext}
              >
                <ChevronRight size={18} aria-hidden="true" />
              </button>
            </div>
            <div
              className="schedule-calendar-scroll"
              role="region"
              aria-label="Selectable seven-day calendar; scroll horizontally to see every day"
              tabIndex={0}
            >
              <div className="schedule-calendar-week">
                {calendar.days.map((day) => (
                  <ScheduleCalendarDay
                    key={day.date}
                    day={day}
                    current={day.date === calendar.range.currentDate}
                    selected={day.date === selectedDate}
                    onSelect={() => onSelectDate(day.date)}
                  />
                ))}
              </div>
            </div>
            {isFetching ? (
              <span className="schedule-calendar-refresh" role="status">
                Refreshing Calendar…
              </span>
            ) : null}
          </>
        )}
      </section>
    );
  },
);

function ScheduleCalendarDay({
  day,
  current,
  selected,
  onSelect,
}: {
  day: CalendarDay;
  current: boolean;
  selected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      data-schedule-date={day.date}
      className={`schedule-calendar-day ${current ? "is-today" : ""} ${
        selected ? "is-selected" : ""
      }`}
      aria-label={calendarDayAccessibleName(day)}
      aria-pressed={selected}
      onClick={onSelect}
    >
      <span className="schedule-calendar-day-heading">
        <span>{formatDayName(day.date)}</span>
        <strong>{formatDayNumber(day.date)}</strong>
        {current ? <i>Today</i> : null}
      </span>
      <span className="schedule-calendar-day-body">
        {day.sessions.length > 0 ? (
          day.sessions.map((session) => (
            <ScheduleCalendarEvent key={session.id} session={session} />
          ))
        ) : (
          <span className="schedule-calendar-empty">
            <span>Open day</span>
            <small>No activity planned</small>
          </span>
        )}
      </span>
      <span className="schedule-calendar-day-load">
        {formatMinutes(day.totalActivityDurationMinutes)} activity time
      </span>
    </button>
  );
}

function ScheduleCalendarEvent({ session }: { session: CalendarSession }): React.JSX.Element {
  return (
    <span className="schedule-calendar-event">
      <span className="schedule-calendar-event-meta">
        <BookOpenCheck size={11} aria-hidden="true" />
        <span>Session</span>
        <span>·</span>
        <span>{session.scheduledTime ?? "Any time"}</span>
      </span>
      <strong>{session.activityTitle}</strong>
      <span className="schedule-calendar-event-footer">
        <span>{formatMinutes(session.activityDurationMinutes)}</span>
        {session.status !== "scheduled" ? (
          <span>{session.status === "completed" ? "Completed" : "Cancelled"}</span>
        ) : null}
      </span>
    </span>
  );
}
